// Module-level types & helpers extracted from Composer.tsx for size discipline.
// Pure data: no React state or hooks.
import type {
  ContentAvatar,
  ContentBrief,
  ContentBriefDraft,
  ContentBriefPlan,
  ContentBriefPublication,
  ContentBriefReference,
  ContentBriefWithReferences,
  ContentChip,
  ContentConnectorType,
  ContentDestinationFormat,
  ContentFramework,
  ContentFrameworkColumn,
  ContentFrameworkPrinciple,
  ContentFrameworkTopic,
  ContentPublishingConnector,
  ContentPublishingDispatch,
  ContentSource,
  ContentVoiceProfile,
  ExcerptReferenceSummary,
  ReadwiseHighlightSummary,
} from "@/lib/composerCore";
import type { BriefPipelineStatus } from "@/components/composer/statusPillTypes";

export type Snapshot = {
  voiceProfile: ContentVoiceProfile | null;
  avatars: ContentAvatar[];
  frameworks: ContentFramework[];
  columns: ContentFrameworkColumn[];
  principles: ContentFrameworkPrinciple[];
  topics: ContentFrameworkTopic[];
  chips: ContentChip[];
  sources: ContentSource[];
  briefs: ContentBrief[];
  briefReferences: ContentBriefReference[];
  briefPlans: ContentBriefPlan[];
  briefDrafts: ContentBriefDraft[];
  briefPublications: ContentBriefPublication[];
  publishingConnectors: ContentPublishingConnector[];
  publishingDispatches: ContentPublishingDispatch[];
  readwiseHighlights: ReadwiseHighlightSummary[];
  excerpts: ExcerptReferenceSummary[];
};

export type BriefDraftState = {
  title: string;
  contentType: string;
  prompt: string;
  notes: string;
};


export const emptySnapshot: Snapshot = {
  voiceProfile: null,
  avatars: [],
  frameworks: [],
  columns: [],
  principles: [],
  topics: [],
  chips: [],
  sources: [],
  briefs: [],
  briefReferences: [],
  briefPlans: [],
  briefDrafts: [],
  briefPublications: [],
  publishingConnectors: [],
  publishingDispatches: [],
  readwiseHighlights: [],
  excerpts: [],
};

// Placeholder cap for framework columns until the per-tier entitlement is wired up
// in src/lib/tier.ts. See .lovable/plan.md.
export const FRAMEWORK_COLUMN_CAP = 4;

export const splitLines = (value: string) =>
  value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

export const countBy = <T,>(items: T[], getKey: (item: T) => string) =>
  items.reduce<Record<string, number>>((acc, item) => {
    const key = getKey(item);
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});



export type PendingDispatch = {
  publicationId: string;
  publicationTitle: string;
  destinationFormat: ContentDestinationFormat;
  connectorId: string;
  connectorLabel: string;
  connectorType: ContentConnectorType;
  recipientSummary: string | null;
};

// ---- Publication clipboard / download helpers (pure) ----
import { toast } from "sonner";
import { contentDestinationFormatExtension } from "@/lib/composerCore";

export const copyPublicationText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  } catch {
    toast.error("Could not copy to clipboard");
  }
};

export const downloadPublicationFile = (
  text: string,
  title: string,
  destinationFormat: ContentDestinationFormat,
) => {
  const ext = contentDestinationFormatExtension[destinationFormat];
  const safe = (title || "publication").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "publication";
  const mime = ext === "html" ? "text/html" : ext === "md" ? "text/markdown" : "text/plain";
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safe}.${ext}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
